import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import logger from "~/utils/logger";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generates avatar initials from a user's name
 * @param name The user's full name
 * @returns Uppercase initials limited to 2 characters
 */
export function getAvatarInitials(name?: string | null): string {
  if (!name) return "";

  return name
    .trim()
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function getAvatarColor(name?: string | null): string {
  if (!name) return "bg-emerald-600";

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const avatarColors = [
    "bg-red-600",
    "bg-orange-600",
    "bg-amber-600",
    "bg-lime-600",
    "bg-green-600",
    "bg-teal-600",
    "bg-cyan-600",
    "bg-blue-600",
    "bg-indigo-600",
    "bg-purple-600",
    "bg-pink-600",
    "bg-slate-600",
  ];

  const index = Math.abs(hash) % avatarColors.length;
  return avatarColors[index];
}

export enum Stages {
  LOCAL = "local",
  DEV = "dev",
  STAGING = "staging",
  PRODUCTION = "production",
}

const currentStage: Stages = import.meta.env.VITE_STAGE as Stages;

export const logCurrentStage = () => {
  if (currentStage !== Stages.PRODUCTION) {
    const styles = {
      local:
        "background: #2563eb; color: white; padding: 2px 6px; border-radius: 4px;",
      dev: "background: #16a34a; color: white; padding: 2px 6px; border-radius: 4px;",
      staging:
        "background: #ca8a04; color: white; padding: 2px 6px; border-radius: 4px;",
    };

    logger.info(
      `%cEnvironment: %c${currentStage}`,
      "font-weight: bold;",
      styles[currentStage as keyof typeof styles] || ""
    );
    logger.info("Environment Variables:", import.meta.env);
  }
};

export const enableForEnv = (envs: Stages[]) => {
  return envs.includes(currentStage);
};
